import React, { useState } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Bot, Send, RefreshCw, FileAudio, Loader2, StickyNote } from 'lucide-react';
import { errMessage } from '../../lib/meetings';

const SendSummaryForm = ({ meeting, onDone }) => {
  const [opts, setOpts] = useState({ to_clients: false, to_me: true, to_leader: false, to_team: false, include_transcript: false });
  const [custom, setCustom] = useState('');
  const [sending, setSending] = useState(false);
  const toggle = (k) => setOpts((o) => ({ ...o, [k]: !o[k] }));
  const send = async () => {
    setSending(true);
    try {
      await api.post(`/meetings/notetaker/meetings/${meeting.id}/send-summary`, {
        ...opts, custom_emails: custom.split(',').map((e) => e.trim()).filter(Boolean),
      });
      toast.success('Summary emails queued — client copies exclude internal content');
      onDone();
    } catch (e) { toast.error(errMessage(e, 'Send failed')); }
    finally { setSending(false); }
  };
  const boxes = [
    ['to_clients', 'Client attendees'], ['to_me', 'Myself'], ['to_leader', 'Team Leader'],
    ['to_team', 'Internal attendees'], ['include_transcript', 'Include transcript (internal only)'],
  ];
  return (
    <div className="rounded-lg border p-2.5 space-y-2" data-testid="send-summary-form">
      <div className="grid grid-cols-2 gap-1.5">
        {boxes.map(([k, label]) => (
          <label key={k} className="flex items-center gap-1.5 text-[11px] cursor-pointer">
            <Checkbox checked={opts[k]} onCheckedChange={() => toggle(k)} data-testid={`send-summary-${k}`} /> {label}
          </label>
        ))}
      </div>
      <Input value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="Custom emails (comma separated)"
        className="h-8 text-xs" data-testid="send-summary-custom-input" />
      <Button size="sm" className="h-8 w-full" onClick={send} disabled={sending} data-testid="send-summary-btn">
        {sending ? <Loader2 size={12} className="animate-spin mr-1.5" /> : <Send size={12} className="mr-1.5" />} Send Summary
      </Button>
    </div>
  );
};

const PasteNotesForm = ({ meeting, onDone }) => {
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!text.trim()) { toast.error('Paste some notes or a transcript first'); return; }
    setSaving(true);
    try {
      await api.post(`/meetings/notetaker/meetings/${meeting.id}/transcript`, { transcript_text: text.trim() });
      toast.success('Notes attached to the meeting');
      onDone();
    } catch (e) { toast.error(errMessage(e, 'Failed to attach notes')); }
    finally { setSaving(false); }
  };
  return (
    <div className="space-y-1.5" data-testid="paste-notes-form">
      <Textarea rows={3} value={text} onChange={(e) => setText(e.target.value)}
        placeholder="Paste notes / transcript here (Hindi + English is fine)…" className="text-xs" data-testid="paste-notes-input" />
      <Button size="sm" variant="outline" className="h-8 w-full" onClick={save} disabled={saving} data-testid="paste-notes-save-btn">
        {saving ? <Loader2 size={12} className="animate-spin mr-1.5" /> : <StickyNote size={12} className="mr-1.5" />} Attach Notes / Transcript
      </Button>
    </div>
  );
};

const AudioUpload = ({ meeting, onDone }) => {
  const [uploading, setUploading] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 200 * 1024 * 1024) { toast.error('Audio exceeds the 200 MB limit'); return; }
    const fd = new FormData();
    fd.append('file', file);
    setUploading(true);
    try {
      await api.post(`/meetings/notetaker/meetings/${meeting.id}/audio`, fd);
      toast.success('Audio recording attached');
      onDone();
    } catch (err) { toast.error(errMessage(err, 'Upload failed')); }
    finally { setUploading(false); e.target.value = ''; }
  };
  return (
    <label className="flex items-center justify-center gap-1.5 rounded-lg border border-dashed px-3 py-2 text-[11px] text-muted-foreground cursor-pointer hover:border-primary/50" data-testid="audio-upload-label">
      {uploading ? <Loader2 size={12} className="animate-spin" /> : <FileAudio size={12} />}
      {meeting.audio_filename ? `Replace audio (${meeting.audio_filename})` : 'Upload audio recording (MP3/M4A/WAV, ≤200 MB)'}
      <input type="file" accept=".mp3,.m4a,.wav" className="hidden" onChange={upload} data-testid="audio-upload-input" />
    </label>
  );
};

/** AI summary / transcript section on the meeting side panel. */
export const TranscriptCard = ({ meeting, onChanged }) => {
  const t = meeting.transcript;
  const [mode, setMode] = useState(null); // send | paste
  const [busy, setBusy] = useState(false);

  const resync = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/meetings/notetaker/meetings/${meeting.id}/resync`);
      data.ok ? toast.success('Re-synced from the notetaker') : toast.info(data.message);
      onChanged();
    } catch (e) { toast.error(errMessage(e, 'Re-sync failed')); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-lg border p-3 space-y-2" data-testid="transcript-card">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold flex items-center gap-1.5"><Bot size={13} className="text-primary" /> AI Notes & Transcript</span>
        <div className="flex items-center gap-1">
          {t?.status === 'Ready' && <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200" data-testid="transcript-ready-badge">Ready · {t.provider}</Badge>}
          <button type="button" onClick={resync} disabled={busy} className="text-muted-foreground hover:text-primary" title="Re-sync from notetaker" aria-label="Re-sync" data-testid="transcript-resync-btn">
            <RefreshCw size={12} className={busy ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {t?.summary_overview && <div className="text-xs"><b>Overview:</b> {t.summary_overview}</div>}
      {(t?.key_points || []).length > 0 && (
        <div className="text-[11px]"><b>Key points:</b>
          <ul className="list-disc pl-4 space-y-0.5 mt-0.5">{t.key_points.slice(0, 6).map((k) => <li key={k.slice(0, 40)}>{k}</li>)}</ul>
        </div>
      )}
      {(t?.action_items || []).length > 0 && (
        <div className="text-[11px]"><b>Next steps:</b>
          <ul className="list-disc pl-4 space-y-0.5 mt-0.5">{t.action_items.slice(0, 6).map((a) => <li key={a.slice(0, 40)}>{a}</li>)}</ul>
        </div>
      )}
      {t?.recording_url && <a href={t.recording_url} target="_blank" rel="noreferrer" className="text-[11px] text-primary hover:underline" data-testid="transcript-recording-link">Open recording</a>}
      {t?.has_transcript_text && (
        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/meetings/notetaker/meetings/${meeting.id}/transcript-text`}
          target="_blank" rel="noreferrer" className="text-[11px] text-primary hover:underline block" data-testid="transcript-download-link">
          View full transcript
        </a>
      )}

      <div className="flex gap-1.5">
        {t?.status === 'Ready' && (
          <Button size="sm" variant="outline" className="h-7 text-[11px] flex-1" onClick={() => setMode(mode === 'send' ? null : 'send')} data-testid="transcript-send-toggle">
            <Send size={11} className="mr-1" /> Send Summary
          </Button>
        )}
        <Button size="sm" variant="outline" className="h-7 text-[11px] flex-1" onClick={() => setMode(mode === 'paste' ? null : 'paste')} data-testid="transcript-paste-toggle">
          <StickyNote size={11} className="mr-1" /> Paste Notes
        </Button>
      </div>
      {mode === 'send' && <SendSummaryForm meeting={meeting} onDone={() => setMode(null)} />}
      {mode === 'paste' && <PasteNotesForm meeting={meeting} onDone={() => { setMode(null); onChanged(); }} />}
      <AudioUpload meeting={meeting} onDone={onChanged} />
    </div>
  );
};
